import React from "react";
import { useSearchParams } from "react-router-dom";

const LOGIN_BY_AREA = { m: "/m/login", doctor: "/doctor", portal: "/portal" };

const COPY = {
  404: {
    code: "404",
    title: "This page has left the circle.",
    body: "The page you're looking for doesn't exist or has moved.",
    cta: ["Back to home", "/"],
  },
  500: {
    code: "500",
    title: "Something went wrong.",
    body: "We hit an unexpected problem. Please reload — your data is safe.",
    cta: ["Reload", null],
  },
  expired: {
    code: "Session ended",
    title: "Please sign in again.",
    body: "For your security, your session has expired or was signed out on another device.",
    cta: ["Sign in again", null],
  },
};

export function StatusView({ kind, loginHref }) {
  const c = COPY[kind];
  const [label, href] = c.cta;
  const go = async () => {
    if (href || loginHref) return window.location.assign(href || loginHref);
    if (kind === "500") await clearOfflineCache();
    window.location.reload();
  };
  return (
    <section className="min-h-[80vh] bg-[var(--lc-cream)] flex items-center" data-testid={`status-page-${kind}`}>
      <div className="mx-auto max-w-[1400px] w-full px-5 lg:px-10 py-24">
        <div className="eyebrow text-[var(--lc-moss)] mb-5">LONGEV CIRCLE · Basel</div>
        <div className="font-serif-display text-[var(--lc-sand)] text-6xl sm:text-7xl font-light leading-none" data-testid="status-page-code">
          {c.code}
        </div>
        <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1] text-[var(--lc-ink)]">{c.title}</h1>
        <p className="mt-5 max-w-md text-[15px] text-[var(--lc-muted)]">{c.body}</p>
        <button
          onClick={go}
          className="mt-10 inline-flex items-center px-7 py-4 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss)] transition-colors"
          data-testid="status-page-cta"
        >
          {label}
        </button>
      </div>
    </section>
  );
}

export function NotFoundPage() {
  return <StatusView kind="404" />;
}

export function SessionExpiredPage() {
  const [params] = useSearchParams();
  return <StatusView kind="expired" loginHref={LOGIN_BY_AREA[params.get("from")] || "/portal"} />;
}

async function clearOfflineCache() {
  try {
    if (window.caches) await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
    if (navigator.serviceWorker) await Promise.all((await navigator.serviceWorker.getRegistrations()).map((r) => r.unregister()));
  } catch {
    /* best effort */
  }
}

function reportError(error, info) {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: String(error?.message || error).slice(0, 1000),
        stack: String(error?.stack || "").slice(0, 8000),
        component_stack: String(info?.componentStack || "").slice(0, 8000),
        path: window.location.pathname,
        build_id: process.env.REACT_APP_BUILD_ID || "",
      }),
    }).catch(() => {});
  } catch {
    /* never throw from the error screen */
  }
}

export class ErrorBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error, info) {
    reportError(error, info);
  }
  render() {
    return this.state.failed ? <StatusView kind="500" /> : this.props.children;
  }
}

export function goToSessionExpired() {
  const p = window.location.pathname;
  if (p.startsWith("/session-expired")) return;
  const from = p.startsWith("/m") ? "m" : p.startsWith("/doctor") ? "doctor" : "portal";
  window.location.replace(`/session-expired?from=${from}`);
}
